import { RecipientContainer } from '../types/RecipientContainer';
import getMemberCountInfo, { MemberCountInfo } from 'owa-mail-tips/lib/utils/getMemberCountInfo';
import AttendeePersonaProps from 'owa-readwrite-recipient-well-fabric/lib/store/schema/AttendeePersonaProps';
import FindRecipientPersonaType from 'owa-recipient-types/lib/types/FindRecipientPersonaType';
import ReadWriteRecipientViewState from 'owa-recipient-types/lib/types/ReadWriteRecipientViewState';
import { isValidSmtpAddress } from 'owa-recipient-email-address/lib/utils/isValidRecipientAddress';
import { SharingTipRecipientInfo } from 'owa-sharing-data';
import { RecipientExpansionStatus } from 'owa-recipient-types/lib/types/RecipientExpansionStatus';
import {
    recipientContainerIsRecipientWell,
    recipientContainerIsAttendeeTypeList,
} from './typeGuards';

export function getSharingTipRecipientInfo(
    recipientContainers: RecipientContainer[],
    fromAddress: string
): SharingTipRecipientInfo[] {
    const recipients: ReadWriteRecipientViewState[] = getRecipientsFromRecipientContainers(
        recipientContainers
    );
    let recipientsInfo: SharingTipRecipientInfo[] = [];
    recipients.forEach(recipient => {
        const persona: FindRecipientPersonaType = recipient.persona;
        const memberCountInfo: MemberCountInfo = getMemberCountInfo(
            fromAddress,
            persona.EmailAddress.EmailAddress
        );
        if (memberCountInfo !== null) {
            recipientsInfo.push({
                recipient: recipient,
                totalMemberCount: memberCountInfo.TotalMemberCount,
                externalMemberCount: memberCountInfo.ExternalMemberCount,
            });
        }
    });

    return recipientsInfo;
}

function getRecipientsFromRecipientContainers(
    recipientContainers: RecipientContainer[]
): ReadWriteRecipientViewState[] {
    if (!recipientContainers || recipientContainers.length === 0) {
        return [];
    }

    let recipients: ReadWriteRecipientViewState[] = [];

    recipientContainers
        .filter(recipientContainer => !!recipientContainer)
        .map(recipientContainer => {
            const newRecipients = getRecipients(recipientContainer);
            recipients = recipients.concat(newRecipients);
        });

    return recipients;
}

function getRecipients(recipientContainer: RecipientContainer): ReadWriteRecipientViewState[] {
    /**
     * recipients.slice() is required since recipients is a mobx observable array
     * and in order to get javascript array we need to use splice
     */
    if (recipientContainerIsRecipientWell(recipientContainer)) {
        return recipientContainer.recipients.slice();
    } else if (recipientContainerIsAttendeeTypeList(recipientContainer)) {
        const attendees = recipientContainer as AttendeePersonaProps[];
        return convertAttendeesToRecipientWellViewStates(attendees);
    }
    return [];
}

function convertAttendeesToRecipientWellViewStates(
    attendees: AttendeePersonaProps[]
): ReadWriteRecipientViewState[] {
    return attendees
        ? attendees.map(attendee => {
              const persona = attendee.persona
                  ? attendee.persona
                  : {
                        EmailAddress: attendee.Mailbox,
                        PersonaId: {
                            // Recipient well code uses the PersonaId.Id in its logic.
                            // In calendar we use Id: attendee.Mailbox.EmailAddress, but we are worried
                            // that in the beautiful links case that incorrect ids will cause exceptions
                            // Since we need the Id, and do not have de-dupe logic, we will use null to make
                            // catching the missing Id cases easier.
                            // This situation occurs when updating an existing event
                            Id: null,
                        },
                    };
              const emailAddress = persona.EmailAddress;
              return {
                  persona: persona,
                  isValid: isValidSmtpAddress(emailAddress.EmailAddress),
                  isSelected: false,
                  isEditing: false,
                  isFadedOut: false,
                  isExpanding: false,
                  isContextMenuOpen: false,
                  expansionStatus: RecipientExpansionStatus.None,
                  members: [],
                  displayText: emailAddress.Name ? emailAddress.Name : emailAddress.EmailAddress,
              } as ReadWriteRecipientViewState;
          })
        : [];
}
